import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import Dropzone from "react-dropzone";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";

import { supabase } from "../../supabaseClient";
import HeaderContainer from "../CrewDashboard/HeaderContainer";
import { ErrorList } from "../common/error";

import {
  easternLocalToUtc,
  endsWithTime,
  getAirlineFromEntry,
  getDropoffTimeFromEntry,
  getFlightNoFromEntry,
  passValidation,
} from "../Utils/import";
import { airportLocalToUtc } from "../../utils/airportTz";
import { parsePdfFile } from "../Import/pdfSchedule";

const ImportXls = () => {
  const navigate = useNavigate();
  const [fileNames, setFileNames] = useState([]);
  const [jsonList, setJsonList] = useState([]);

  const [isProcessingCSV, setProcessingCSV] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [errorList, setErrorList] = useState([]);

  const handleBack = useCallback(() => {
    sessionStorage.getItem("UserRole") === "1"
      ? navigate("/super-admin")
      : navigate("/manager");
  }, [navigate]);

  // Parse one workbook to its transformed schedule rows. Returns the rows
  // and a list of validation errors so the caller can merge per-file results
  // into one import batch. Resolves with empty rows on any parse failure
  // (the error toast is fired here so the caller doesn't double-report).
  const parseWorkbookFile = useCallback((file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          const worksheetName = workbook.SheetNames[1] ?? workbook.SheetNames[0];
          if (!worksheetName) {
            resolve({ jsonData: [], errorData: [] });
            return;
          }
        // Array-of-arrays so header row position doesn't matter (the previous
        // "header: 18" quirk assumed a fixed row and silently failed on the
        // newer Southwest layout, which has headers at row 9 instead of 18).
        // We scan by content to find the two header rows and lock in column
        // indices for the data rows below.
        const xlsData = XLSX.utils.sheet_to_json(
          workbook.Sheets[worksheetName],
          { header: 1, defval: null, raw: false }
        );

        // Detect the owning airline from the sheet title (API/APH publishes
        // one file per client airline, but flight prefixes inside can be
        // codeshare/regional carriers — e.g. an Air Canada schedule lists
        // AC mainline plus ZX (Air Georgian) and RV (Rouge) rows, all
        // billed to Air Canada). Force every parsed row's client to the
        // sheet owner regardless of the flight prefix.
        let sheetAirlineCode = null;
        const titleText = xlsData
          .slice(0, 12)
          .flat()
          .map((c) => String(c || ""))
          .join(" ");
        if (/jetBlue Ground Transportation Schedule/i.test(titleText)) sheetAirlineCode = "B6";
        else if (/Air Canada Ground Transportation Schedule/i.test(titleText)) sheetAirlineCode = "AC";
        else if (/Southwest Airlines Ground Transportation Schedule/i.test(titleText)) sheetAirlineCode = "WN";
        else if (/Horizon Air Ground Transportation Schedule/i.test(titleText)) sheetAirlineCode = "QX";
        else if (/Condor.*Ground Transportation Schedule/i.test(titleText)) sheetAirlineCode = "DE";
        else if (/PSA Airlines Ground Transportation Schedule/i.test(titleText)) sheetAirlineCode = "OH";
        else if (/Endeavor Air Ground Transportation Schedule/i.test(titleText)) sheetAirlineCode = "9E";
        else if (/Republic Airways Ground Transportation Schedule/i.test(titleText)) sheetAirlineCode = "YX";
        else if (/Envoy.*Ground Transportation Schedule/i.test(titleText)) sheetAirlineCode = "MQ";
        else if (/SkyWest.*Ground Transportation Schedule/i.test(titleText)) sheetAirlineCode = "OO";
        else if (/Frontier.*Ground Transportation Schedule/i.test(titleText)) sheetAirlineCode = "F9";
        else if (/Spirit.*Ground Transportation Schedule/i.test(titleText)) sheetAirlineCode = "NK";
        else if (/GoJet.*Ground Transportation Schedule/i.test(titleText)) sheetAirlineCode = "G7";
        else if (/Aeromexico.*Ground Transportation Schedule/i.test(titleText)) sheetAirlineCode = "AM";

        let startIndex = 0;
        let dateIdx = -1,
          departmentIdx = -1,
          pickupIdx = -1,
          dropoffIdx = -1,
          airportIdx = -1,
          dropoffAirportIdx = -1,
          passengerIdx = -1,
          pickupTimeIdx = -1;
        for (let i = 0; i < xlsData.length; i++) {
          const row = xlsData[i] || [];
          const indexOfAll = (val) => {
            const out = [];
            for (let k = 0; k < row.length; k++) if (row[k] === val) out.push(k);
            return out;
          };
          if (
            row.includes("DATE") &&
            row.includes("DEPARTMENT") &&
            row.includes("#RIDERS")
          ) {
            dateIdx = row.indexOf("DATE");
            departmentIdx = row.indexOf("DEPARTMENT");
            passengerIdx = row.indexOf("#RIDERS");
          } else if (
            row.includes("Location") &&
            row.includes("From") &&
            row.includes("Pickup Date/Time") &&
            row.includes("To")
          ) {
            const locs = indexOfAll("Location");
            airportIdx = locs[0];
            dropoffAirportIdx = locs[1] ?? locs[0]; // second Location col for dropoff
            pickupTimeIdx = row.indexOf("Pickup Date/Time");
            pickupIdx = row.indexOf("From");
            dropoffIdx = row.indexOf("To");
            startIndex = i + 1;
            break;
          }
        }
        if (
          dateIdx < 0 ||
          departmentIdx < 0 ||
          pickupIdx < 0 ||
          dropoffIdx < 0 ||
          airportIdx < 0 ||
          passengerIdx < 0
        ) {
          console.error(`Excel format not supported for ${file.name}`);
          toast.error(`Unsupported xls format: ${file.name}`);
          resolve({ jsonData: [], errorData: [] });
          return;
        }

        // Accept either "01-Aug-2026" (legacy JetBlue xls) or "2026-08-01"
        // (new Southwest xls). Sheetjs may also hand us a Date object when a
        // cell is date-formatted; dayjs handles that too.
        const parseXlsDate = (v) => {
          if (v == null || v === "") return null;
          if (v instanceof Date) return dayjs(v);
          const s = String(v).trim();
          const iso = dayjs(s, "YYYY-MM-DD", true);
          if (iso.isValid()) return iso;
          const legacy = dayjs(s, "DD-MMM-YYYY", true);
          if (legacy.isValid()) return legacy;
          const loose = dayjs(s);
          return loose.isValid() ? loose : null;
        };

        // Southwest xls repeats the DATE/DEPARTMENT/#RIDERS header block every
        // ~32 rows (page break). Skip those repeated headers; they otherwise
        // sneak through the `!!row[departmentIdx]` filter as truthy strings.
        const rawJsonData = xlsData.filter((row, index) => {
          if (index < startIndex) return false;
          if (!row) return false;
          const dept = row[departmentIdx];
          if (!dept) return false;
          if (dept === "DEPARTMENT") return false;
          if (dept === "PICK UP" || dept === "DROP OFF") return false;
          return true;
        });
        const jsonData = [];
        const errorData = [];

        let curDate = rawJsonData[0]?.[dateIdx];

        for (const _row of rawJsonData) {
          if (_row[dateIdx]) curDate = _row[dateIdx];
          const scheduleDate = parseXlsDate(curDate);
          if (!scheduleDate) {
            errorData.push(_row);
            continue;
          }
          const passenger = _row[passengerIdx];
          const airport = _row[airportIdx];
          const dropoffAirport = _row[dropoffAirportIdx];
          const pickup = _row[pickupIdx];
          const dropoff = _row[dropoffIdx];
          // The legacy xls "Pickup Date/Time" column carried the van pickup
          // time (e.g. "01 May 04:00"). Newer Southwest xls files leave this
          // column blank — the pickup runs on the flight time itself. Both
          // are handled: a value is parsed in the airport's own timezone,
          // absence becomes null.
          const rowAirportForPickup = (airport || "").toUpperCase();
          const pickupTimeRaw = pickupTimeIdx >= 0 ? _row[pickupTimeIdx] : null;
          let pickupDateTime = null;
          if (pickupTimeRaw && `${pickupTimeRaw}`.trim()) {
            const MONTHS_IDX = { Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11 };
            const s = `${pickupTimeRaw}`.trim();
            const m = s.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{1,2}):(\d{2})$/);
            if (m) {
              const mon = m[2][0].toUpperCase() + m[2].slice(1, 3).toLowerCase();
              const monthIdx = MONTHS_IDX[mon];
              if (monthIdx != null) {
                pickupDateTime = airportLocalToUtc(
                  rowAirportForPickup,
                  scheduleDate.year(),
                  monthIdx,
                  parseInt(m[1], 10),
                  parseInt(m[3], 10),
                  parseInt(m[4], 10)
                );
              }
            }
          }
          const newItem = {
            scheduleDate,
            passenger,
            airport,
            pickup,
            dropoff,
            pickupDateTime,
            dropoffAirport,
          };
          if (!passValidation(newItem)) errorData.push(_row);
          let transformed = {
            company: "ACT",
            status: "ACTIVE",
            pilots: 0,
            flightCrew: 0,
            code: "",
            rate: 0,
            airport: newItem.airport,
            passenger: newItem.passenger,
          };
          const rowAirportCode = (newItem.airport || "").toUpperCase();
          if (endsWithTime(newItem.pickup)) {
            const pickupSplit = newItem.pickup.split("-");
            const dropoffHotel = newItem.dropoff;
            const airline = getAirlineFromEntry(pickupSplit);
            const flightNo = getFlightNoFromEntry(pickupSplit);
            const time = getDropoffTimeFromEntry(
              pickupSplit,
              scheduleDate.year(),
              rowAirportCode
            );
            transformed.dropoffLocation = dropoffHotel;
            transformed.pickupLocation = newItem.airport;
            transformed.flightNo = flightNo;
            transformed.scheduleDate = time;
            transformed.pickupDateTime = newItem.pickupDateTime;
            transformed.pass = newItem.passenger;
            transformed.client = airline;
            transformed.type = "ARRIVE";
          } else if (endsWithTime(newItem.dropoff)) {
            const dropoffSplit = newItem.dropoff.split("-");
            const pickupHotel = newItem.pickup;
            const dropoffAirline = getAirlineFromEntry(dropoffSplit);
            const dropoffFlightNo = getFlightNoFromEntry(dropoffSplit);
            const dropoffTime = getDropoffTimeFromEntry(
              dropoffSplit,
              scheduleDate.year(),
              rowAirportCode
            );
            transformed.dropoffLocation = newItem.airport;
            transformed.pickupLocation = pickupHotel;
            transformed.flightNo = dropoffFlightNo;
            transformed.scheduleDate = dropoffTime || scheduleDate.toDate();
            transformed.pickupDateTime = newItem.pickupDateTime;
            transformed.pass = newItem.passenger;
            transformed.client = dropoffAirline;
            transformed.type = "DEPART";
          } else {
            throw new Error("Invalid pickup/dropoff location format");
          }
          // Force the client to the sheet's owning airline. An Air Canada
          // schedule may list AC / ZX / RV flight prefixes; a JetBlue sheet
          // may list codeshare partners; all are billed to the sheet owner.
          if (sheetAirlineCode) transformed.client = sheetAirlineCode;
          // Set airport location and vehicle based on airport
          const clientUpper = transformed.client.toUpperCase();
          transformed.airportCode = newItem.airport.toUpperCase(); // Store airport code for manager lookup
          
          switch (newItem.airport.toUpperCase()) {
            case "LAX":
              transformed.vehicle = "1002";
              if (clientUpper === "B6") transformed.rate = 62;
              if (clientUpper === "QX") transformed.rate = 65;
              if (clientUpper === "AS") transformed.rate = 65;
              break;
            case "BUR":
              transformed.vehicle = "1002";
              if (clientUpper === "B6") transformed.rate = 44;
              break;
            case "SDF":
              transformed.vehicle = "2200";
              if (clientUpper === "WN") transformed.rate = 73;
              break;
            case "LAS":
              transformed.vehicle = "1701";
              if (clientUpper === "B6") transformed.rate = 39.43;
              if (clientUpper === "RV") transformed.rate = 38;
              if (clientUpper === "AM") transformed.rate = 54;
              if (clientUpper === "AC") transformed.rate = 38;
              break;
            case "MCO":
              transformed.vehicle = "1500";
              if (clientUpper === "B6") transformed.rate = 40.8;
              if (clientUpper === "WN") transformed.rate = 60;
              break;
            case "MSP":
              transformed.vehicle = "2001";
              if (clientUpper === "WN") transformed.rate = 70;
              break;
            case "CVG":
              transformed.vehicle = "2100";
              if (clientUpper === "WN") transformed.rate = 95;
              break;
            case "PHL":
              transformed.vehicle = "1201";
              if (clientUpper === "F9") transformed.rate = 48;
              break;
            case "DAY":
              transformed.vehicle = "601";
              if (clientUpper === "ENDEAVOR") transformed.rate = 100;
              if (clientUpper === "9E") transformed.rate = 70;
              break;
            case "BUF":
              transformed.vehicle = "2300";
              transformed.rate = 70;
              break;
            case "RDU":
              transformed.vehicle = "2401";
              // Rate from LocationAirlineRates (B6 @ RDU = $69).
              break;
            default:
              console.warn(`Airport ${newItem.airport} is not configured - using default values`);
              transformed.vehicle = "DEFAULT"; // Default vehicle code
              transformed.rate = 0;
          }
          
          // Manager will be assigned during import based on airport location

          jsonData.push(transformed);
        }
        resolve({ jsonData, errorData });
      } catch (_err) {
        console.error(`Error reading ${file.name}`, _err);
        toast.error(`Error while reading ${file.name}`);
        resolve({ jsonData: [], errorData: [] });
      }
    };
    reader.onerror = () => {
      console.error(`FileReader error on ${file.name}`);
      toast.error(`Error while reading ${file.name}`);
      resolve({ jsonData: [], errorData: [] });
    };

    reader.readAsArrayBuffer(file);
  });
}, []);

const onDrop = useCallback(
  async (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    setProcessingCSV(true);
    try {
      // Dispatch by extension — PDFs go through the pdfjs-based parser,
      // xls/xlsx through the existing workbook parser. Both return the
      // same {jsonData, errorData} shape so the downstream import logic
      // is unchanged.
      const results = await Promise.all(
        acceptedFiles.map((f) =>
          /\.pdf$/i.test(f.name) ? parsePdfFile(f) : parseWorkbookFile(f)
        )
      );
      setFileNames(acceptedFiles.map((f) => f.name));
      setJsonList(results.flatMap((r) => r.jsonData));
      setErrorList(results.flatMap((r) => r.errorData));
    } finally {
      setProcessingCSV(false);
    }
  },
  [parseWorkbookFile]
);

  const onImportSuccess = useCallback(() => {
    if (sessionStorage.getItem("UserRole") === "1")
      navigate("/super-admin#schedule-table");
    else navigate("/manager#schedule-table");
  }, [navigate]);
  // Function to check and create missing locations
  const ensureLocationsExist = async (locationCodes) => {
    try {
      // Check which locations already exist
      const { data: existingLocations, error: fetchError } = await supabase
        .from("Locations")
        .select("Code")
        .in("Code", locationCodes);

      if (fetchError) {
        console.error("Error fetching existing locations:", fetchError);
        return;
      }

      const existingCodes = existingLocations?.map((loc) => loc.Code) || [];
      const missingCodes = locationCodes.filter(
        (code) => !existingCodes.includes(code)
      );

      if (missingCodes.length === 0) {
        console.log("All locations already exist");
        return;
      } else {
        console.log("Missing locations that will be created:", missingCodes);
      }

      // Get the next available LocationID
      const { data: maxLocation, error: maxError } = await supabase
        .from("Locations")
        .select("LocationID")
        .order("LocationID", { ascending: false })
        .limit(1);

      if (maxError) {
        console.error("Error getting max LocationID:", maxError);
        return;
      }

      const nextLocationID = (maxLocation?.[0]?.LocationID || 0) + 1;

      // Create missing locations - determine type based on code
      const knownAirports = ['LAX', 'BUR', 'SDF', 'LAS', 'MCO', 'MSP', 'CVG', 
                             'PHL', 'DAY', 'BUF', 'JFK', 'EWR', 'LGA', 'ORD', 
                             'ATL', 'DFW', 'DEN', 'SEA', 'SFO', 'BOS', 'MIA', 
                             'PHX', 'IAH', 'DTW', 'CLT'];
      
      const locationsToCreate = missingCodes.map((code, index) => ({
        LocationID: nextLocationID + index,
        Code: code,
        Description: code,
        LocationTypeID: knownAirports.includes(code.toUpperCase()) ? 2 : 1, // 2 for airports, 1 for hotels
        CompanyID: 1, // Default company ID - adjust as needed
        MinutesFromAirport: 0, // Default value
      }));

      console.log("Creating locations:", locationsToCreate);
      
      const { data: insertedLocations, error: insertError } = await supabase
        .from("Locations")
        .insert(locationsToCreate)
        .select();

      if (insertError) {
        console.error("Error creating locations:", insertError);
        toast.error(`Failed to create locations: ${insertError.message}`);
      } else {
        console.log(
          `Successfully created ${insertedLocations?.length || 0} locations:`,
          insertedLocations
        );
        toast.success(`Created ${missingCodes.length} new locations`);
      }
    } catch (error) {
      console.error("Error ensuring locations exist:", error);
    }
  };

  // Function to check and create missing airlines
  const ensureAirlinesExist = async (airlineCodes) => {
    try {
      // Check which airlines already exist
      const { data: existingAirlines, error: fetchError } = await supabase
        .from("Airlines")
        .select("AirlineLetterCode")
        .in("AirlineLetterCode", airlineCodes);

      if (fetchError) {
        console.error("Error fetching existing airlines:", fetchError);
        return;
      }

      const existingCodes =
        existingAirlines?.map((airline) => airline.AirlineLetterCode) || [];
      const missingCodes = airlineCodes.filter(
        (code) => !existingCodes.includes(code)
      );

      if (missingCodes.length === 0) {
        console.log("All airlines already exist");
        return;
      }

      // Create missing airlines
      const airlinesToCreate = missingCodes.map((code) => ({
        AirlineLetterCode: code,
        Code: code,
        Description: code,
        CompanyID: 1,
        Active: 1,
      }));

      const { error: insertError } = await supabase
        .from("Airlines")
        .insert(airlinesToCreate);

      if (insertError) {
        console.error("Error creating airlines:", insertError);
        toast.error("Failed to create some airlines");
      } else {
        console.log(
          `Successfully created ${missingCodes.length} airlines:`,
          missingCodes
        );
        toast.success(`Created ${missingCodes.length} new airlines`);
      }
    } catch (error) {
      console.error("Error ensuring airlines exist:", error);
    }
  };

  // Function to check and create missing vehicles
  const ensureVehiclesExist = async (vehicleCodes) => {
    try {
      const { data: existingVehicles, error: fetchError } = await supabase
        .from("Vehicles")
        .select("Code")
        .in("Code", vehicleCodes);

      if (fetchError) {
        console.error("Error fetching existing vehicles:", fetchError);
        return;
      }

      const existingCodes = existingVehicles?.map((v) => v.Code) || [];
      const missingCodes = vehicleCodes.filter(
        (code) => !existingCodes.includes(code)
      );

      if (missingCodes.length === 0) return;

      const vehiclesToCreate = missingCodes.map((code) => ({
        Code: code,
        Make: "Default",
        Model: `Vehicle ${code}`,
        CompanyID: 1,
        VehicleStatusID: 1,
      }));

      const { error: insertError } = await supabase
        .from("Vehicles")
        .insert(vehiclesToCreate);

      if (insertError) {
        console.error("Error creating vehicles:", insertError);
        toast.error(`Failed to create vehicles: ${insertError.message}`);
      } else {
        console.log(`Created ${missingCodes.length} vehicles:`, missingCodes);
        toast.success(`Created ${missingCodes.length} new vehicles`);
      }
    } catch (error) {
      console.error("Error ensuring vehicles exist:", error);
    }
  };

  const onImport = useCallback(async () => {
    setIsImporting(true);
    const now = dayjs();

    try {
      // Collect unique codes from parsed data
      const airlineCodes = [...new Set(jsonList.map((i) => i.client).filter(Boolean))];
      const companyCodes = [...new Set(jsonList.map((i) => i.company).filter(Boolean))];
      const vehicleCodes = [...new Set(jsonList.map((i) => i.vehicle).filter(Boolean))];
      const transportCodes = [...new Set(jsonList.map((i) => i.type).filter(Boolean))];
      const statusCodes = [...new Set(jsonList.map((i) => i.status).filter(Boolean))];
      const pickupCodes = [...new Set(jsonList.map((i) => i.pickupLocation).filter(Boolean))];
      const dropoffCodes = [...new Set(jsonList.map((i) => i.dropoffLocation).filter(Boolean))];
      const allLocationCodes = [...new Set([...pickupCodes, ...dropoffCodes])];

      // Auto-create missing reference data
      console.log("Ensuring locations, airlines, and vehicles exist...");
      await ensureLocationsExist(allLocationCodes);
      await ensureAirlinesExist(airlineCodes);
      await ensureVehiclesExist(vehicleCodes);

      // Build lookup dictionaries (replaces edge function lookups)
      const buildLookup = async (table, keyField, valueField, codes) => {
        const dict = {};
        if (!codes || codes.length === 0) return dict;
        const { data, error } = await supabase
          .from(table)
          .select(`${keyField}, ${valueField}`)
          .in(keyField, codes);
        if (error) console.error(`Lookup error for ${table}:`, error);
        if (data) data.forEach((row) => { dict[row[keyField]] = row[valueField]; });
        return dict;
      };

      const buildVehicleLookup = async (codes) => {
        const dict = {};
        if (!codes || codes.length === 0) return dict;
        for (const code of codes) {
          const { data } = await supabase
            .from("Vehicles")
            .select("VehicleID, Code")
            .ilike("Code", `${code}%`)
            .limit(1)
            .maybeSingle();
          if (data) dict[code] = data.VehicleID;
        }
        return dict;
      };

      const getManagerLookup = async () => {
        const lookup = {};
        const { data: userLocations } = await supabase
          .from("UserLocations")
          .select("UserID, Locations:LocationID (Code)")
          .in("UserID", (await supabase
            .from("Users")
            .select("UserID")
            .eq("RoleID", 2)
            .eq("Active", 1)
          ).data?.map((u) => u.UserID) || []);
        if (userLocations) {
          for (const ul of userLocations) {
            const code = ul.Locations?.Code;
            if (code) lookup[code] = ul.UserID;
          }
        }
        return lookup;
      };

      // Fetch default rates from LocationAirlineRates table
      const getRateLookup = async () => {
        const rateLookup = {};
        const { data: rateRows } = await supabase
          .from("LocationAirlineRates")
          .select("LocationID, AirlineID, Rate, Locations:LocationID (Code), Airlines:AirlineID (AirlineLetterCode)");
        if (rateRows) {
          for (const row of rateRows) {
            const locCode = row.Locations?.Code;
            const airCode = row.Airlines?.AirlineLetterCode || "*";
            if (locCode) {
              rateLookup[`${locCode.toUpperCase()}|${airCode.toUpperCase()}`] = parseFloat(row.Rate);
            }
          }
        }
        return rateLookup;
      };

      const getDefaultRate = (airportCode, airlineCode, rateLookup) => {
        if (!airportCode) return null;
        const key = `${airportCode.toUpperCase()}|${(airlineCode || "").toUpperCase()}`;
        if (rateLookup[key] !== undefined) return rateLookup[key];
        const wildcard = `${airportCode.toUpperCase()}|*`;
        if (rateLookup[wildcard] !== undefined) return rateLookup[wildcard];
        return null;
      };

      // Cross-station rates are keyed by (fromLocationID, toLocationID, airlineID).
      // Loaded once and looked up in the row loop for A2A shuttles.
      const getCrossStationLookup = async () => {
        const dict = {};
        const { data } = await supabase
          .from("CrossStationRates")
          .select("FromLocationID, ToLocationID, AirlineID, Rate");
        if (data) for (const r of data) {
          const key = `${r.FromLocationID}|${r.ToLocationID}|${r.AirlineID || "*"}`;
          dict[key] = parseFloat(r.Rate);
        }
        return dict;
      };
      // Bidirectional CSR lookup — most van rates are symmetric (the drive
      // is the same in either direction), so we accept a match keyed either
      // way. Falls back to a wildcard airline entry before giving up.
      const getCrossStationRate = (fromLocID, toLocID, airlineID, lookup) => {
        if (!fromLocID || !toLocID) return null;
        const airKey = String(airlineID || "*");
        for (const [a, b] of [[fromLocID, toLocID], [toLocID, fromLocID]]) {
          const k = `${a}|${b}|${airKey}`;
          if (lookup[k] !== undefined) return lookup[k];
          const w = `${a}|${b}|*`;
          if (lookup[w] !== undefined) return lookup[w];
        }
        return null;
      };

      const [airlineDict, companyDict, vehicleDict, transportDict, statusDict, locationDict, managerLookup, rateLookup, crossStationLookup] =
        await Promise.all([
          buildLookup("Airlines", "AirlineLetterCode", "AirlineID", airlineCodes),
          buildLookup("Companies", "Name", "CompanyID", companyCodes),
          buildVehicleLookup(vehicleCodes),
          buildLookup("ScheduleTransportationTypes", "Code", "TransportationTypeID", transportCodes),
          buildLookup("ScheduleStatuses", "Code", "ScheduleStatusID", statusCodes),
          buildLookup("Locations", "Code", "LocationID", allLocationCodes),
          getManagerLookup(),
          getRateLookup(),
          getCrossStationLookup(),
        ]);

      console.log("Lookup dicts built:", {
        airlines: Object.keys(airlineDict).length,
        companies: Object.keys(companyDict).length,
        vehicles: Object.keys(vehicleDict).length,
        transports: Object.keys(transportDict).length,
        statuses: Object.keys(statusDict).length,
        locations: Object.keys(locationDict).length,
        managers: Object.keys(managerLookup).length,
      });

      // Build schedule rows with FK IDs, validate each row
      const schedulesList = [];
      const validationErrors = [];

      for (let idx = 0; idx < jsonList.length; idx++) {
        const item = jsonList[idx];
        const missingKeys = [];
        if (!companyDict[item.company]) missingKeys.push(`Company: ${item.company}`);
        if (!airlineDict[item.client]) missingKeys.push(`Airline: ${item.client}`);
        if (!vehicleDict[item.vehicle]) missingKeys.push(`Vehicle: ${item.vehicle}`);
        if (!transportDict[item.type]) missingKeys.push(`Transport: ${item.type}`);
        if (!statusDict[item.status]) missingKeys.push(`Status: ${item.status}`);
        if (!locationDict[item.pickupLocation]) missingKeys.push(`Pickup: ${item.pickupLocation}`);
        if (!locationDict[item.dropoffLocation]) missingKeys.push(`Dropoff: ${item.dropoffLocation}`);

        if (missingKeys.length > 0) {
          validationErrors.push(`Row ${idx + 1}: Missing - ${missingKeys.join(", ")}`);
          continue;
        }

        const airport = item.airportCode;
        // Prefer the row's own airport manager. Fall back to the PDF's
        // "home airport" manager for positioning rows where the pickup
        // is a remote airport that isn't staffed (e.g. JB MCO PDF has
        // TPA→Hilton Orlando rows that Luis at MCO actually runs).
        let managerId = airport && managerLookup[airport] ? managerLookup[airport] : null;
        if (!managerId && item.homeAirport && managerLookup[item.homeAirport]) {
          managerId = managerLookup[item.homeAirport];
        }

        if (!managerId && airport) {
          validationErrors.push(
            `Row ${idx + 1}: No manager assigned to airport "${airport}"` +
            (item.homeAirport ? ` (nor to home airport "${item.homeAirport}")` : "") +
            `. Go to Managers → assign the airport code to a manager.`
          );
          continue;
        }

        // Rate resolution:
        //   1. Explicit cross-station shuttle (A2A, two airports on the row):
        //      look up CrossStationRates by (pickupLoc, dropoffLoc, airline).
        //   2. Positioning row — airport-to-hotel where the pickup airport
        //      is NOT the PDF's home airport (e.g. TPA → Hilton Orlando in
        //      the MCO PDF): treat it as a cross-station drive and look up
        //      CSR between the pickup airport and the home airport.
        //   3. Otherwise fall back to LocationAirlineRates as before.
        const puID = locationDict[item.pickupLocation];
        const doID = locationDict[item.dropoffLocation];
        const homeLocID = item.homeAirport ? locationDict[item.homeAirport] : null;
        const isPositioning =
          !item.isCrossStation &&
          item.homeAirport &&
          item.airportCode &&
          item.airportCode !== item.homeAirport &&
          homeLocID;

        let rate;
        if (item.isCrossStation) {
          // For hotel-pickup cross-station rows (e.g. "Hilton Orlando → PBI")
          // the puID is a hotel LocationID and no CSR row will match. Fall
          // back on (origin airport → dropoff airport) using item.airportCode,
          // which is where the seeded CrossStationRates actually live. For
          // hotel-to-hotel across-airport rows, the destination airport comes
          // from item._crossStationToAirport (dropoff is a hotel name).
          const originAirportLocID = locationDict[item.airportCode];
          const destAirportLocID = item._crossStationToAirport
            ? locationDict[item._crossStationToAirport]
            : doID;
          rate =
            getCrossStationRate(puID, doID, airlineDict[item.client], crossStationLookup)
            ?? getCrossStationRate(originAirportLocID, destAirportLocID, airlineDict[item.client], crossStationLookup)
            ?? item.rate
            ?? getDefaultRate(item.airportCode, item.client, rateLookup)
            ?? null;
        } else if (isPositioning) {
          rate =
            getCrossStationRate(puID, homeLocID, airlineDict[item.client], crossStationLookup)
            ?? item.rate
            ?? getDefaultRate(item.airportCode, item.client, rateLookup)
            ?? null;
        } else {
          rate =
            item.rate
            || getDefaultRate(item.airportCode, item.client, rateLookup)
            || null;
        }

        schedulesList.push({
          CompanyID: companyDict[item.company],
          ScheduleStatusID: statusDict[item.status],
          ScheduleTypeID: 1,
          TransportationTypeID: transportDict[item.type],
          AirlineID: airlineDict[item.client],
          VehicleID: vehicleDict[item.vehicle],
          ScheduleDate: item.scheduleDate,
          PickupDateTime: item.pickupDateTime || null,
          FlightNo: item.flightNo || "",
          RateOverride: rate,
          CreatedDate: new Date().toISOString(),
          ...(managerId ? { ManagerID: managerId } : {}),
          // Internal fields for pickup/dropoff creation (stripped before insert)
          _PickupLocationID: puID,
          _DropoffLocationID: doID,
          _Passengers: item.pass || 1,
        });
      }

      if (validationErrors.length > 0) {
        console.error("Validation errors:", validationErrors);
        toast.error(`${validationErrors.length} rows failed validation. Check console for details.`);
        setErrorList(validationErrors);
        setIsImporting(false);
        return;
      }

      if (schedulesList.length === 0) {
        toast.error("No valid schedules to import");
        setIsImporting(false);
        return;
      }

      // Drop rows whose ScheduleDate didn't parse — these were producing a
      // "Invalid time value" crash in the toISOString() call below. Log
      // them so the operator can spot the source PDF row in console.
      const droppedInvalid = [];
      const validList = schedulesList.filter((s, idx) => {
        const t = new Date(s.ScheduleDate).getTime();
        if (Number.isFinite(t)) return true;
        droppedInvalid.push({ idx, FlightNo: s.FlightNo, ScheduleDate: s.ScheduleDate });
        return false;
      });
      if (droppedInvalid.length > 0) {
        console.warn("Rows skipped (invalid ScheduleDate):", droppedInvalid);
        for (const d of droppedInvalid) {
          validationErrors.push(
            `Row ${d.idx + 1}: invalid ScheduleDate (FlightNo "${d.FlightNo}"). Row skipped.`
          );
        }
      }
      if (validList.length === 0) {
        if (droppedInvalid.length > 0) {
          toast.error(`${droppedInvalid.length} rows had invalid dates. Check console.`);
          setErrorList(validationErrors);
        } else {
          toast.error("No valid schedules to import");
        }
        setIsImporting(false);
        return;
      }

      // Batch-internal duplicate collapse — ACES (and some other operators)
      // routinely publish 2-3 identical rows for the same trip, and separately
      // 2-3 rows for the same flight with 5-min-apart pickup times that are
      // really the same shuttle. The user isn't billed for these duplicates,
      // so collapse them before the DB-existence check runs. Same-flight rows
      // with meaningfully different ScheduleDate values, or different pickup/
      // dropoff LocationIDs (same flight servicing crew at two different
      // hotels — LAX B6 1224 landing at Sonesta AND Doubletree), stay as
      // separate rows.
      const batchKey = (s) =>
        `${new Date(s.ScheduleDate).toISOString()}|${(s.FlightNo || "").trim()}|${s.AirlineID}|${s.TransportationTypeID}|${s._PickupLocationID || ""}|${s._DropoffLocationID || ""}`;
      const seenInBatch = new Set();
      const batchDeduped = [];
      let intraBatchDropped = 0;
      for (const s of validList) {
        const k = batchKey(s);
        if (seenInBatch.has(k)) { intraBatchDropped++; continue; }
        seenInBatch.add(k);
        batchDeduped.push(s);
      }
      if (intraBatchDropped > 0) {
        console.log(
          `Skipping ${intraBatchDropped} duplicate rows found within this import file.`
        );
      }
      const rowsForDbCheck = batchDeduped;

      // Per-row duplicate check: ScheduleDate + FlightNo + AirlineID
      // Find the date range of the import to query existing schedules.
      const allDates = rowsForDbCheck.map((s) => new Date(s.ScheduleDate).getTime());
      const minDate = new Date(Math.min(...allDates)).toISOString().slice(0, 10);
      const maxDate = new Date(Math.max(...allDates) + 86400000).toISOString().slice(0, 10);
      const importAirlineIds = [...new Set(rowsForDbCheck.map((s) => s.AirlineID))];

      // Pull existing schedules AND their SchedulePickups/ScheduleDropoffs
      // so re-imports that carry same-flight-different-hotel rows aren't
      // collapsed into a single "already exists" match. The dedup key
      // matches the batch key: date + flight + airline + type + pu + do.
      //
      // Supabase caps a single request at 1000 rows by default. A full-month
      // Southwest import can easily overlap 5000+ existing Schedule rows, so
      // we page through explicitly — otherwise the DB-existence set only
      // covers the first 1000 rows and every other existing row re-inserts
      // as a duplicate. Same for SchedulePickups / ScheduleDropoffs.
      const PAGE = 1000;
      const existingSchedules = [];
      for (let off = 0; ; off += PAGE) {
        const { data, error } = await supabase
          .from("Schedule")
          .select("ScheduleID, ScheduleDate, FlightNo, AirlineID, TransportationTypeID")
          .in("AirlineID", importAirlineIds)
          .gte("ScheduleDate", minDate)
          .lt("ScheduleDate", maxDate)
          .order("ScheduleID", { ascending: true })
          .range(off, off + PAGE - 1);
        if (error) { console.error("existingSchedules page failed:", error); break; }
        if (!data || data.length === 0) break;
        existingSchedules.push(...data);
        if (data.length < PAGE) break;
      }
      console.log(`Existing schedules in date range: ${existingSchedules.length}`);

      // Fetch SchedulePickups/ScheduleDropoffs for those IDs, in ID chunks
      // (a giant .in() list exceeds URL length) and paged per chunk.
      const fetchLocByScheduleIDs = async (table, ids) => {
        const map = new Map();
        for (let i = 0; i < ids.length; i += 500) {
          const chunk = ids.slice(i, i + 500);
          for (let off = 0; ; off += PAGE) {
            const { data, error } = await supabase
              .from(table)
              .select("ScheduleID, LocationID")
              .in("ScheduleID", chunk)
              .range(off, off + PAGE - 1);
            if (error) { console.error(`${table} page failed:`, error); break; }
            if (!data || data.length === 0) break;
            for (const r of data) if (!map.has(r.ScheduleID)) map.set(r.ScheduleID, r.LocationID);
            if (data.length < PAGE) break;
          }
        }
        return map;
      };

      const existingKeys = new Set();
      if (existingSchedules.length) {
        const ids = existingSchedules.map((s) => s.ScheduleID);
        const [puMap, doMap] = await Promise.all([
          fetchLocByScheduleIDs("SchedulePickups", ids),
          fetchLocByScheduleIDs("ScheduleDropoffs", ids),
        ]);
        for (const s of existingSchedules) {
          const d = new Date(s.ScheduleDate).toISOString();
          const base = `${d}|${(s.FlightNo || "").trim()}|${s.AirlineID}|${s.TransportationTypeID}`;
          const pu = puMap.get(s.ScheduleID) || "";
          const doo = doMap.get(s.ScheduleID) || "";
          existingKeys.add(`${base}|${pu}|${doo}`);
        }
        console.log(`Existing dedup keys built: ${existingKeys.size}`);
      }

      const deduped = rowsForDbCheck.filter((s) => {
        const d = new Date(s.ScheduleDate).toISOString();
        const base = `${d}|${(s.FlightNo || "").trim()}|${s.AirlineID}|${s.TransportationTypeID}`;
        const key = `${base}|${s._PickupLocationID || ""}|${s._DropoffLocationID || ""}`;
        return !existingKeys.has(key);
      });

      const dbSkipped = rowsForDbCheck.length - deduped.length;
      if (dbSkipped > 0) {
        console.log(`Skipping ${dbSkipped} rows already in the DB`);
      }
      const skippedCount = intraBatchDropped + dbSkipped;

      if (deduped.length === 0) {
        const msg = intraBatchDropped > 0
          ? `${intraBatchDropped} duplicate rows removed. Remaining ${rowsForDbCheck.length} schedules already exist.`
          : `All ${rowsForDbCheck.length} schedules already exist. Nothing to import.`;
        toast.info(msg);
        setIsImporting(false);
        return;
      }

      // Replace schedulesList with deduped for the insert phase
      const schedulesToInsert = deduped;

      // Batch insert schedules in chunks of 50
      let successCount = 0;
      const insertErrors = [];
      const chunkSize = 50;

      for (let i = 0; i < schedulesToInsert.length; i += chunkSize) {
        const batch = schedulesToInsert.slice(i, i + chunkSize);

        // Strip internal underscore-prefixed fields before inserting
        const insertBatch = batch.map(({ _PickupLocationID, _DropoffLocationID, _Passengers, ...rest }) => rest);

        try {
          const { data: inserted, error: insertError } = await supabase
            .from("Schedule")
            .insert(insertBatch)
            .select();

          if (insertError) {
            insertErrors.push(`Batch ${Math.floor(i / chunkSize) + 1}: ${insertError.message}`);
            continue;
          }

          if (inserted && inserted.length > 0) {
            successCount += inserted.length;

            // Build pickup and dropoff rows keyed by returned ScheduleID
            const pickups = [];
            const dropoffs = [];
            for (let j = 0; j < inserted.length; j++) {
              const schedule = inserted[j];
              const original = batch[j];

              if (original._PickupLocationID) {
                pickups.push({
                  ScheduleID: schedule.ScheduleID,
                  LocationID: original._PickupLocationID,
                  Passengers: original._Passengers,
                });
              }
              if (original._DropoffLocationID) {
                dropoffs.push({
                  ScheduleID: schedule.ScheduleID,
                  LocationID: original._DropoffLocationID,
                });
              }
            }

            if (pickups.length > 0) {
              const { error: pickupError } = await supabase
                .from("SchedulePickups")
                .insert(pickups);
              if (pickupError) insertErrors.push(`Pickup batch: ${pickupError.message}`);
            }

            if (dropoffs.length > 0) {
              const { error: dropoffError } = await supabase
                .from("ScheduleDropoffs")
                .insert(dropoffs);
              if (dropoffError) insertErrors.push(`Dropoff batch: ${dropoffError.message}`);
            }
          }
        } catch (err) {
          insertErrors.push(`Batch ${Math.floor(i / chunkSize) + 1}: ${err.message}`);
        }
      }

      // Report results
      setIsImporting(false);
      const diffTime = dayjs().diff(now, "second");
      console.log(`Import completed in ${diffTime}s: ${successCount} imported, ${insertErrors.length} errors`);

      const skipMsg = skippedCount > 0 ? ` (${skippedCount} duplicates skipped)` : "";
      if (insertErrors.length === 0 && successCount > 0) {
        toast.success(`Imported ${successCount} schedules successfully!${skipMsg}`);
        onImportSuccess();
      } else if (insertErrors.length > 0 && successCount > 0) {
        toast.info(`Imported ${successCount} schedules with ${insertErrors.length} errors.${skipMsg}`);
        setErrorList(insertErrors);
      } else if (insertErrors.length > 0) {
        toast.error(`Import failed with ${insertErrors.length} errors.${skipMsg}`);
        setErrorList(insertErrors);
      }
    } catch (err) {
      console.error("Import error:", err);
      toast.error(`Import failed: ${err.message}`);
      setIsImporting(false);
    }
  }, [jsonList, onImportSuccess]);

  return (
    <div className="p-6 w-full">
      <HeaderContainer />
      <div className="items-center mt-5 personal-info-content">
        <div
          onClick={handleBack}
          className="flex justify-between cursor-pointer items-center w-20 back-button"
        >
          <FaArrowLeft />
          <span>Back</span>
        </div>
        <p className="pt-4 font-semibold text-lg">Import Data</p>
        <span>
          Easily upload driver, vehicle, and flight data in bulk. Ensure
          accuracy by using the provided template format.
        </span>
        <div className="w-100 mt-4 rounded-lg">
          <div className="flex mb-2">
            <div>
              <p>Upload</p>
            </div>
            <div className="flex-1 flex-center">
              <Dropzone onDrop={onDrop} multiple>
                {({ getRootProps, getInputProps }) => (
                  <section className="dropzone">
                    <div
                      {...getRootProps()}
                      className="w-full h-full flex-center"
                    >
                      <input
                        {...getInputProps()}
                        multiple
                        accept={[
                          "application/vnd.ms-excel",
                          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                          "application/pdf",
                          ".pdf",
                          ".xls",
                          ".xlsx",
                        ].join(",")}
                      />
                      <p>Click to upload or drag and drop</p>
                      <div>File(s) (xls, xlsx, pdf)</div>
                    </div>
                  </section>
                )}
              </Dropzone>
              {fileNames.length > 0 ? (
                <div className="flex flex-col gap-1">
                  {fileNames.map((name) => (
                    <div key={name} className="file-name-label">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M10.0003 18.3337C14.5837 18.3337 18.3337 14.5837 18.3337 10.0003C18.3337 5.41699 14.5837 1.66699 10.0003 1.66699C5.41699 1.66699 1.66699 5.41699 1.66699 10.0003C1.66699 14.5837 5.41699 18.3337 10.0003 18.3337Z"
                          stroke="#00AF06"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6.45801 9.99993L8.81634 12.3583L13.5413 7.6416"
                          stroke="#00AF06"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>{name}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="mt-4 import-buttons">
          <button
            onClick={onImport}
            disabled={isImporting || isProcessingCSV || jsonList.length == 0}
            className={`justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button border-none ${
              isImporting || isProcessingCSV || jsonList.length == 0
                ? "opacity-60"
                : ""
            } hover:opacity-60`}
          >
            {isImporting ? (
              <span className="loader"></span>
            ) : (
              <span>Import</span>
            )}
          </button>
          <div>
            <div
              onClick={() => {
                navigate("/super-admin#schedule-table");
              }}
              className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
            >
              <span>Cancel</span>
            </div>
          </div>
        </div>
        <ErrorList list={errorList} />
      </div>
    </div>
  );
};

export default ImportXls;
